import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ModuleData } from '../../../content';
import { FamilyDefinition } from '../../../content/taxonomy';
import { AppState } from '../../../app/types';
import { GroupId } from '../../../config/systemConfig';
import { getModuleColors } from '../../../config/designTokens';
import { getModuleIcon, getSkillsForModule, getFamiliesForModuleSkill } from '../../../content';
import { getFamilyMasteryKey, getGrowthStage } from '../../../mastery';
import { getDecayStatus, getDecayPercentage } from '../../../mastery/masteryDecay';
import { DjayGrowthState } from '../../shared/DjayGrowthState';
import { LevelUpShockwave } from '../../shared/LevelUpShockwave';
import { usePrevious } from '../../../app/hooks/usePrevious';
import { playSoundIntent } from '../../../utils/sound/soundIntent';
import { RADIUS } from '../../../config/designTokens';
import { Sparkles } from 'lucide-react';
interface TrailStopProps {
  module: ModuleData;
  families: FamilyDefinition[];
  state: AppState;
  selectedGroup: GroupId;
  align: 'left' | 'right' | 'center';
}
export function TrailStop({
  module,
  families,
  state,
  selectedGroup,
  align
}: TrailStopProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const colors = getModuleColors(module.color);
  const ModuleIcon = getModuleIcon(module.icon);
  const skills = getSkillsForModule(module.id);
  let totalScore = 0,
    count = 0;
  // Aggregate ghosting signal: surface the most-faded family so the module
  // node visibly "spookt" when something underneath needs practice.
  const decayRank = {
    fresh: 0,
    recent: 1,
    fading: 2,
    stale: 3
  };
  let worstDecayStatus: 'fresh' | 'recent' | 'fading' | 'stale' = 'fresh';
  let worstDecayPct = 0;
  families.forEach(f => {
    const key = getFamilyMasteryKey(f.familyId, selectedGroup);
    const data = state.familyMastery?.[key];
    if (data) {
      totalScore += data.masteryScore;
      count++;
      const status = getDecayStatus(data);
      if (decayRank[status] > decayRank[worstDecayStatus]) {
        worstDecayStatus = status;
      }
      worstDecayPct = Math.max(worstDecayPct, getDecayPercentage(data));
    }
  });
  const moduleMastery = count > 0 ? Math.round(totalScore / count) : 0;
  const growthStage = getGrowthStage(moduleMastery);
  // State-up celebration: when the module's Djay crosses into a higher growth
  // level, fire a one-shot shockwave + level-up sound over the node icon.
  const prevLevel = usePrevious(growthStage.level);
  const [burstKey, setBurstKey] = useState<number | null>(null);
  useEffect(() => {
    if (prevLevel !== undefined && growthStage.level > prevLevel) {
      setBurstKey(Date.now());
      playSoundIntent('reward.levelUp');
    }
  }, [growthStage.level, prevLevel]);
  // Determine alignment classes
  const alignClass = align === 'left' ? 'self-start ml-4 sm:ml-12' : align === 'right' ? 'self-end mr-4 sm:mr-12' : 'self-center';
  return <div className={`flex flex-col relative z-10 w-full max-w-[280px] sm:max-w-[320px] ${alignClass}`} data-id="element-1998" data-mp="392t3awukn7v1">
      
      <motion.button whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} onClick={() => setIsExpanded(!isExpanded)} className={`relative flex items-center gap-4 p-3 sm:p-4 bg-white border-4 ${colors.border} ${RADIUS.card} shadow-md hover:shadow-lg transition-shadow text-left`} aria-expanded={isExpanded} aria-label={`${module.title}, niveau ${growthStage.label}`} data-id="element-1999" data-mp="3dvcejn5sdqwo">
        
        {/* Node Icon */}
        <div className="relative flex-shrink-0" data-id="element-2000" data-mp="ly9s7znyztm">
          <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center ${colors.light} border-2 ${colors.border}`} data-id="element-2001" data-mp="3hwxp31uult3p">
            
            <ModuleIcon className={`w-7 h-7 sm:w-8 sm:h-8 ${colors.text}`} strokeWidth={2.5} data-id="element-2002" data-mp="3mx274wbemvw1" />
            
          </div>
          <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-0.5 shadow-sm" data-id="element-2003" data-mp="2bfu4afgepntz">
            <DjayGrowthState masteryScore={moduleMastery} size="sm" decayStatus={worstDecayStatus} decayPercentage={worstDecayPct} data-id="element-2004" data-mp="2rmykxf3n2u37" />
            
          </div>
          {/* State-up celebration burst over the growth node */}
          {burstKey !== null && <LevelUpShockwave key={burstKey} level={growthStage.level} onComplete={() => setBurstKey(null)} data-id="element-2005" data-mp="2hxx5pr4x4gqa" />}
        </div>

        {/* Node Info */}
        <div className="flex-1 min-w-0" data-id="element-2006" data-mp="3alt0gqaxfykf">
          <h3 className="text-base sm:text-lg font-black text-slate-800 leading-tight mb-1 truncate" data-id="element-2007" data-mp="3akrpsmxtpe38">
            {module.title}
          </h3>
          <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-bold uppercase tracking-wider ${colors.light} ${colors.text}`} data-id="element-2008" data-mp="35l57hz3517nk">
            
            {growthStage.label}
          </span>
        </div>
      </motion.button>

      {/* Expanded Sub-skills (Stepping Stones) */}
      <AnimatePresence data-id="element-2009" data-mp="1k9n43d91nuzp">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden pt-3 pl-6 sm:pl-8" data-id="element-2010" data-mp="1k8lnyud68ib4">
          
            <div className="flex flex-col gap-3 border-l-4 border-dashed border-slate-200 pl-4 py-2" data-id="element-2011" data-mp="9oqxh96cq9l1">
              {skills.map(skill => {
            const skillFamilies = getFamiliesForModuleSkill(module.id, skill);
            const activeFamilies = skillFamilies.filter(f => {
              const key = getFamilyMasteryKey(f.familyId, selectedGroup);
              return state.familyMastery?.[key] !== undefined;
            });
            if (activeFamilies.length === 0) return null;
            return <div key={skill} className="space-y-2" data-id="element-2012" data-mp="xebh7uabxavd">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-2013" data-mp="2u7mw1eggej6g">
                      {skill}
                    </h4>
                    <div className="space-y-2" data-id="element-2014" data-mp="2z79hgb6fr5qc">
                      {activeFamilies.map(f => {
                  const key = getFamilyMasteryKey(f.familyId, selectedGroup);
                  const data = state.familyMastery?.[key];
                  const score = data?.masteryScore || 0;
                  const gs = getGrowthStage(score);
                  // Calculate 7-day delta
                  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                  let recentCorrect = 0;
                  data?.history?.forEach(h => {
                    if (h.timestamp >= weekAgo && h.isCorrect) recentCorrect++;
                  });
                  return <div key={f.familyId} className="relative" data-id="element-2015" data-mp="3juftl57ry3qp">
                            <div className={`flex items-center gap-3 p-2 bg-white border-2 border-slate-100 ${RADIUS.button} shadow-sm`} data-id="element-2016" data-mp="3jwtpthe6xlzu">
                            
                              <DjayGrowthState masteryScore={score} size="sm" decayStatus={getDecayStatus(data)} decayPercentage={getDecayPercentage(data)} data-id="element-2017" data-mp="3ttohbgc1qm4m" />
                            
                              <div className="flex-1 min-w-0" data-id="element-2018" data-mp="cb99adkf57t0">
                                <p className="text-xs font-bold text-slate-700 truncate" data-id="element-2019" data-mp="gbjdplskbq5r">
                                  {f.description || f.learningGoal}
                                </p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-2020" data-mp="7b4padkaxuy1">
                                  {gs.label}
                                </p>
                              </div>
                            </div>

                            {/* Glowing Footsteps / Recent Growth */}
                            {recentCorrect > 0 && <motion.div initial={{
                      scale: 0.8,
                      opacity: 0
                    }} animate={{
                      scale: 1,
                      opacity: 1
                    }} className="absolute -right-2 -top-2 bg-emerald-100 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-black flex items-center gap-1 shadow-sm z-10" data-id="element-2021" data-mp="3frzh8xzk9uxc">
                            
                                <Sparkles className="w-3 h-3" data-id="element-2022" data-mp="3m89qfze7i656" />↑{' '}
                                {recentCorrect}
                              </motion.div>}
                          </div>;
                })}
                    </div>
                  </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}